/**
 * Market-shape model — pure functions that turn a contest's flat liquidity list
 * plus its market definitions into rendered "books". See docs/API-NOTES.md
 * "Market-shape model". Everything here is side-effect-free and unit-tested
 * (books.test.ts): classification, spread perspective math, consensus-default
 * line, the liquidity filter, tab ordering, and the best-edge scan.
 *
 * Types are structural subsets of the client's `LiquidityPosition` /
 * `PartnerLiquidity` / `Market` so tests can build tiny fixtures and the real
 * (passthrough) shapes drop straight in.
 */

/* ------------------------------------------------------------------- shapes */

export interface PartnerLike {
  partner_id: string;
  partner_name?: string | null;
  price?: number | null;
  /** Native American odds string (for the ¢/%/American display toggle). */
  american_odds?: string | null;
  available?: number | null;
  liquidity_hash?: string;
  edges?: Record<string, unknown>;
}

export interface PositionLike {
  position_hash: string;
  title?: string | null;
  market_key?: string;
  market_label?: string;
  side_key?: string;
  variable?: string | null;
  participant_id?: string | null;
  participant_name?: string | null;
  consensus_line?: string | null;
  consensus_price?: number | null;
  partner_liquidities?: PartnerLike[];
}

export interface MarketLike {
  key?: string;
  label?: string;
  category?: string | null;
  variable_required?: boolean;
  allow_multiple_participants?: boolean;
}

/** The four book layouts. `prop` covers futures/outrights and player props. */
export type MarketShape = "moneyline" | "spread" | "total" | "prop";

/* ------------------------------------------------------- liquidity filtering */

/** A partner book is "real" only when it quotes a positive price AND size. */
export function partnerHasLiquidity(pl: PartnerLike | undefined | null): boolean {
  return (
    !!pl &&
    typeof pl.price === "number" &&
    pl.price > 0 &&
    typeof pl.available === "number" &&
    pl.available > 0
  );
}

/** A position counts only if some partner entry has real liquidity. */
export function positionHasLiquidity(pos: PositionLike): boolean {
  return (pos.partner_liquidities ?? []).some(partnerHasLiquidity);
}

/* -------------------------------------------------------------- classifier */

/** Lowercased set of `side_key`s present on a group of positions. */
export function sidesOf(positions: PositionLike[]): Set<string> {
  const s = new Set<string>();
  for (const p of positions) if (p.side_key) s.add(p.side_key.toLowerCase());
  return s;
}

/**
 * Classify a market_key group into a book shape from its market definition
 * (+ the sides its positions carry to tell spread from total). A missing market
 * def is treated as moneyline-like.
 *   - allow_multiple_participants        → prop (futures/outright/player prop)
 *   - !variable_required                 → moneyline
 *   - variable_required + over/under     → total
 *   - variable_required + home/away/else → spread
 */
export function classifyShape(
  market: MarketLike | undefined,
  positions: PositionLike[],
): MarketShape {
  if (market?.allow_multiple_participants) return "prop";
  if (!(market?.variable_required ?? false)) return "moneyline";
  const sides = sidesOf(positions);
  if (sides.has("over") || sides.has("under")) return "total";
  return "spread";
}

/* --------------------------------------------------------------- tab model */

export interface BookTab {
  /** The market_key this tab is built from. */
  marketKey: string;
  label: string;
  shape: MarketShape;
  /** Positions with real liquidity only, in source order. */
  positions: PositionLike[];
}

const SHAPE_RANK: Record<MarketShape, number> = {
  moneyline: 0,
  spread: 1,
  total: 2,
  prop: 3,
};

/**
 * Group positions by market_key, classify each group, drop groups with no live
 * liquidity, and order the tabs canonically: moneyline, spread, total, then the
 * remaining (prop) market_keys alphabetically by label. Returns the list of
 * market_keys that had no market definition to join against (caller logs once).
 */
export function buildTabs(
  positions: PositionLike[],
  markets: MarketLike[],
): { tabs: BookTab[]; unjoinedKeys: string[] } {
  const marketByKey = new Map<string, MarketLike>();
  for (const m of markets) if (m.key) marketByKey.set(m.key, m);

  const byKey = new Map<string, PositionLike[]>();
  const order: string[] = [];
  for (const p of positions) {
    const k = p.market_key ?? "(unknown)";
    let group = byKey.get(k);
    if (!group) {
      group = [];
      byKey.set(k, group);
      order.push(k);
    }
    group.push(p);
  }

  const tabs: BookTab[] = [];
  const unjoinedKeys: string[] = [];
  for (const key of order) {
    const group = byKey.get(key)!;
    const live = group.filter(positionHasLiquidity);
    if (live.length === 0) continue;
    const market = marketByKey.get(key);
    if (!market) unjoinedKeys.push(key);
    const shape = classifyShape(market, live);
    const label = market?.label ?? live[0]?.market_label ?? key;
    tabs.push({ marketKey: key, label, shape, positions: live });
  }

  tabs.sort(
    (a, b) => SHAPE_RANK[a.shape] - SHAPE_RANK[b.shape] || a.label.localeCompare(b.label),
  );
  return { tabs, unjoinedKeys };
}

/* --------------------------------------------------------- line normalizer */

/**
 * Normalize a spread line to the HOME perspective. The away side of a spread is
 * quoted as the mirror line, so an away line = −variable; round to 0.1 so the two
 * sides of one book collapse to a single line key.
 */
export function normalizeSpreadLine(
  variable: string | null | undefined,
  side: string | undefined,
): number | null {
  if (variable == null || variable === "") return null;
  const v = Number(variable);
  if (!Number.isFinite(v)) return null;
  const home = (side ?? "").toLowerCase() === "away" ? -v : v;
  return round1(home);
}

/** Raw total line (over/under share the same variable) rounded to 0.1. */
export function totalLine(variable: string | null | undefined): number | null {
  if (variable == null || variable === "") return null;
  const v = Number(variable);
  if (!Number.isFinite(v)) return null;
  return round1(v);
}

export interface LineGroup {
  /** Stringified normalized line, e.g. "-3.5" — stable chip key. */
  key: string;
  line: number;
  positions: PositionLike[];
}

/**
 * Group spread positions into one book per normalized (home-perspective) line.
 * Only live positions are grouped; lines are sorted ascending.
 */
export function groupSpreadLines(positions: PositionLike[]): LineGroup[] {
  return groupByLine(positions, (p) => normalizeSpreadLine(p.variable, p.side_key));
}

/** Group total positions into one book per raw line (over/under together). */
export function groupTotalLines(positions: PositionLike[]): LineGroup[] {
  return groupByLine(positions, (p) => totalLine(p.variable));
}

function groupByLine(
  positions: PositionLike[],
  lineOf: (p: PositionLike) => number | null,
): LineGroup[] {
  const map = new Map<number, PositionLike[]>();
  for (const p of positions) {
    if (!positionHasLiquidity(p)) continue;
    const line = lineOf(p);
    if (line == null) continue;
    let arr = map.get(line);
    if (!arr) {
      arr = [];
      map.set(line, arr);
    }
    arr.push(p);
  }
  return [...map.entries()]
    .map(([line, ps]) => ({ key: String(line), line, positions: ps }))
    .sort((a, b) => a.line - b.line);
}

/**
 * Pick the default line chip: the line whose value is closest to `consensusLine`.
 * Ties break to the lower line; with no usable consensus, falls back to the first
 * (lowest) line. Returns the chip `key`, or null when there are no lines.
 */
export function defaultLineKey(
  lines: LineGroup[],
  consensusLine: number | null | undefined,
): string | null {
  if (lines.length === 0) return null;
  // Must already be in the SAME perspective as `lines` — see consensusLineOf.
  const c = consensusLine ?? Number.NaN;
  if (!Number.isFinite(c)) return lines[0].key;
  let best = lines[0];
  let bestD = Math.abs(lines[0].line - c);
  for (const l of lines.slice(1)) {
    const d = Math.abs(l.line - c);
    if (d < bestD) {
      bestD = d;
      best = l;
    }
  }
  return best.key;
}

/**
 * The line chip to show: the user's pick wins for as long as that line still has liquidity,
 * otherwise the consensus-closest line.
 *
 * Pure and derived-on-render BY DESIGN. Defaulting through a `useEffect` keyed on the line
 * groups re-fires on every liquidity refresh (30s poll, or any stream push) — each one
 * rebuilds the array — and silently resets the user's selection mid-trade.
 */
export function resolveLineKey(
  lines: LineGroup[],
  selected: string | null,
  consensus: number | null,
): string | null {
  if (selected != null && lines.some((l) => l.key === selected)) return selected;
  return defaultLineKey(lines, consensus);
}

/**
 * The participant to show on a prop book: the user's pick wins for as long as that
 * participant still quotes, otherwise the first. Same reasoning as {@link resolveLineKey}.
 */
export function resolveParticipantId(
  participants: ParticipantOption[],
  selected: string | null,
): string | null {
  if (selected != null && participants.some((p) => p.id === selected)) return selected;
  return participants[0]?.id ?? null;
}

/**
 * The consensus line for a group of positions, normalized into the SAME perspective the
 * line groups use: home-perspective for spreads, raw for totals.
 *
 * The normalizing matters. `consensus_line` is side-signed exactly like `variable` — on a
 * Seahawks-by-3 game the home positions carry `"-3"` and the away positions `"3"` — so
 * taking the first one raw and comparing it against home-normalized group lines picks the
 * MIRROR line (a 2x-the-line error: +3 instead of -3). Normalize by the side that carried it.
 */
export function consensusLineOf(
  positions: PositionLike[],
  kind: "spread" | "total",
): number | null {
  for (const p of positions) {
    const raw = p.consensus_line;
    if (raw == null || raw === "") continue;
    const line = kind === "spread" ? normalizeSpreadLine(raw, p.side_key) : totalLine(raw);
    if (line != null) return line;
  }
  return null;
}

/* ----------------------------------------------------------- prop / futures */

const SIDE_RANK: Record<string, number> = { yes: 0, over: 0, home: 0, no: 1, under: 1, away: 1 };

/** Distinct live sides on a prop/futures market, affirmative side first. */
export function propSides(positions: PositionLike[]): string[] {
  const s = new Set<string>();
  for (const p of positions) {
    if (positionHasLiquidity(p) && p.side_key) s.add(p.side_key.toLowerCase());
  }
  return [...s].sort(
    (a, b) => (SIDE_RANK[a] ?? 9) - (SIDE_RANK[b] ?? 9) || a.localeCompare(b),
  );
}

export interface ParticipantOption {
  id: string;
  name: string;
}

/** Distinct participants that have real liquidity, sorted by name. */
export function propParticipants(positions: PositionLike[]): ParticipantOption[] {
  const map = new Map<string, string>();
  for (const p of positions) {
    if (!positionHasLiquidity(p)) continue;
    const id = p.participant_id ?? p.position_hash;
    if (!map.has(id)) map.set(id, p.participant_name ?? p.title ?? id);
  }
  return [...map.entries()]
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** Largest available size across a position's live partner books (0 if none). */
export function bestAvailable(pos: PositionLike): number {
  let best = 0;
  for (const pl of pos.partner_liquidities ?? []) {
    if (partnerHasLiquidity(pl) && typeof pl.available === "number" && pl.available > best) {
      best = pl.available;
    }
  }
  return best;
}

/**
 * Rows for a prop/futures book: one live position per participant on the chosen
 * `side` (e.g. every team's "will win" quote), sorted by participant name.
 *
 * The API can return MORE than one position per participant+side (distinct
 * market_side_ids for the same economic outcome — see FRICTION.md), so we dedupe
 * by participant, keeping the most-liquid book, to avoid identical duplicate rows.
 */
export function propRows(positions: PositionLike[], side: string): PositionLike[] {
  const byParticipant = new Map<string, PositionLike>();
  for (const p of positions) {
    if ((p.side_key ?? "").toLowerCase() !== side || !positionHasLiquidity(p)) continue;
    const id = p.participant_id ?? p.position_hash;
    const prev = byParticipant.get(id);
    if (!prev || bestAvailable(p) > bestAvailable(prev)) byParticipant.set(id, p);
  }
  return [...byParticipant.values()].sort((a, b) =>
    (a.participant_name ?? a.title ?? "").localeCompare(b.participant_name ?? b.title ?? ""),
  );
}

/* ----------------------------------------------------- transposed book grid */

/**
 * Column order within a book: the affirmative/left side first (home | over | yes),
 * its complement second (away | under | no). Anything unknown sorts last, stably.
 */
const COLUMN_SIDE_RANK: Record<string, number> = {
  home: 0, over: 0, yes: 0, away: 1, under: 1, no: 1,
};

export interface BookGridColumn {
  /** side_key, lowercased — the stable column key. */
  side: string;
  /** Display label: team + signed line, "Over 2.5"/"Under 2.5", or "Yes"/"No". */
  label: string;
  /** The (most-liquid) position backing this side — source of cells + consensus. */
  position: PositionLike;
  consensusPrice: number | null;
  consensusLine: string | null;
  /** Cheapest (lowest) live venue price in this column = best to buy (null if none). */
  bestPrice: number | null;
}

export interface BookGridRow {
  partnerId: string;
  partnerName: string;
  /** One entry per column (aligned by index); undefined where the venue doesn't
   *  price that side, so the cell renders an em-dash rather than a gap. */
  cells: (PartnerLike | undefined)[];
}

export interface BookGrid {
  columns: BookGridColumn[];
  rows: BookGridRow[];
}

/**
 * Transpose ONE book slice into a venue-row / side-column grid.
 *
 * `slice` is the set of positions that make up a single book: the two sides of a
 * moneyline, of a chosen spread/total line, or one participant's yes/no on a prop.
 * Columns are the distinct sides in stable order (home|over|yes, then away|under|no);
 * rows are the venues carrying real liquidity on at least one side of the slice,
 * sorted by name. Best price is computed **per column** (per side). Duplicate
 * positions for one side (distinct market_side_ids — see FRICTION.md) collapse to
 * the most-liquid book. Pure + unit-tested (see buildBookGrid in books.test.ts).
 */
export function buildBookGrid(slice: PositionLike[]): BookGrid {
  // One position per side; dedupe dup market_side_ids by keeping the richest book.
  const bySide = new Map<string, PositionLike>();
  for (const p of slice) {
    const side = (p.side_key ?? "").toLowerCase();
    if (!side) continue;
    const prev = bySide.get(side);
    if (!prev || bestAvailable(p) > bestAvailable(prev)) bySide.set(side, p);
  }

  const sides = [...bySide.keys()].sort(
    (a, b) => (COLUMN_SIDE_RANK[a] ?? 9) - (COLUMN_SIDE_RANK[b] ?? 9) || a.localeCompare(b),
  );

  // Row set = venues with real liquidity on at least one side of the slice.
  const venueName = new Map<string, string>();
  for (const side of sides) {
    for (const pl of bySide.get(side)!.partner_liquidities ?? []) {
      if (partnerHasLiquidity(pl) && !venueName.has(pl.partner_id)) {
        venueName.set(pl.partner_id, pl.partner_name ?? pl.partner_id);
      }
    }
  }
  const venues = [...venueName.entries()]
    .map(([partnerId, partnerName]) => ({ partnerId, partnerName }))
    .sort((a, b) => a.partnerName.localeCompare(b.partnerName));

  const columns: BookGridColumn[] = sides.map((side) => {
    const position = bySide.get(side)!;
    let bestPrice: number | null = null;
    for (const pl of position.partner_liquidities ?? []) {
      if (
        partnerHasLiquidity(pl) &&
        typeof pl.price === "number" &&
        (bestPrice === null || pl.price < bestPrice)
      ) {
        bestPrice = pl.price;
      }
    }
    return {
      side,
      label: sideColumnLabel(position, side),
      position,
      consensusPrice: position.consensus_price ?? null,
      consensusLine: position.consensus_line ?? null,
      bestPrice,
    };
  });

  const rows: BookGridRow[] = venues.map(({ partnerId, partnerName }) => ({
    partnerId,
    partnerName,
    cells: columns.map((col) =>
      (col.position.partner_liquidities ?? []).find((pl) => pl.partner_id === partnerId),
    ),
  }));

  return { columns, rows };
}

/** Column header for a side: team + signed line, Over/Under + line, or Yes/No. */
export function sideColumnLabel(pos: PositionLike, side: string): string {
  const s = side.toLowerCase();
  if (s === "yes") return "Yes";
  if (s === "no") return "No";
  const line = lineMagnitude(pos.variable);
  if (s === "over") return line != null ? `Over ${line}` : "Over";
  if (s === "under") return line != null ? `Under ${line}` : "Under";
  // home/away (moneyline/spread) or any other participant side.
  const base = pos.participant_name ?? pos.title ?? titleCase(s);
  const signed = signedLine(pos.variable);
  return signed ? `${base} ${signed}` : base;
}

/** Absolute line magnitude for over/under headers, e.g. "2.5". Null when 0/blank. */
function lineMagnitude(variable: string | null | undefined): string | null {
  if (variable == null || variable === "") return null;
  const v = Number(variable);
  if (!Number.isFinite(v) || v === 0) return null;
  return String(round1(Math.abs(v)));
}

/** Signed spread line with a real minus glyph, e.g. "+1.5" / "−1.5". Null when 0/blank. */
function signedLine(variable: string | null | undefined): string | null {
  if (variable == null || variable === "") return null;
  const v = round1(Number(variable));
  if (!Number.isFinite(v) || v === 0) return null;
  return v > 0 ? `+${v}` : `−${Math.abs(v)}`;
}

function titleCase(s: string): string {
  return s.replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/* ------------------------------------------------------------- best-edge scan */

export interface BestEdge {
  positionHash: string;
  marketKey: string;
  title?: string | null;
  participantName?: string | null;
  side?: string;
  variable?: string | null;
  partnerId: string;
  partnerName?: string | null;
  liquidityHash?: string;
  edgeKey: string;
  /** Fractional edge (e.g. 0.07 = 7%). */
  edge: number;
  price: number | null;
}

/**
 * Scan every partner book's `edges` map and return the single best opportunity:
 * the max numeric edge in (0, 0.20], ignoring model-noise (>0.20) and any entry
 * without real liquidity. Returns null when nothing qualifies (edges are usually
 * empty live). The result deep-links to (market, position, participant, venue).
 */
export function scanBestEdge(positions: PositionLike[]): BestEdge | null {
  let best: BestEdge | null = null;
  for (const pos of positions) {
    for (const pl of pos.partner_liquidities ?? []) {
      if (!partnerHasLiquidity(pl)) continue;
      const edges = pl.edges;
      if (!edges || typeof edges !== "object") continue;
      for (const [edgeKey, raw] of Object.entries(edges)) {
        const edge = typeof raw === "number" ? raw : Number(raw);
        if (!Number.isFinite(edge) || edge <= 0 || edge > 0.2) continue;
        if (best === null || edge > best.edge) {
          best = {
            positionHash: pos.position_hash,
            marketKey: pos.market_key ?? "(unknown)",
            title: pos.title,
            participantName: pos.participant_name,
            side: pos.side_key,
            variable: pos.variable,
            partnerId: pl.partner_id,
            partnerName: pl.partner_name,
            liquidityHash: pl.liquidity_hash,
            edgeKey,
            edge,
            price: typeof pl.price === "number" ? pl.price : null,
          };
        }
      }
    }
  }
  return best;
}

/* --------------------------------------------------------------------- utils */

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}
